import * as R from 'ramda'
import { useEffect, useMemo, useState } from 'react'

import useAxios from 'hooks/useAxios'
import useDebounce from 'hooks/useDebounce'

const DEBOUNCE_MS = 400
const REQUEST_TIMEOUT_MS = 10000

const ADDRESS_INCOMPLETE_PREVIEW: TaxPreview = {
  status: 'requires_location_inputs',
  currency: null,
  subtotal: null,
  discount: null,
  tax: null,
  tax_rate_percent: null,
  total: null,
}

export interface TaxPreviewAddress {
  country?: string | null
  state?: string | null
  postal_code?: string | null
  line1?: string | null
  line2?: string | null
  city?: string | null
}

export interface TaxPreview {
  status: 'complete' | 'requires_location_inputs' | 'failed' | 'not_applicable'
  currency?: string | null
  subtotal?: number | null
  discount?: number | null
  tax?: number | null
  tax_rate_percent?: number | null
  total?: number | null
}

interface UseTaxPreviewParams {
  enabled: boolean
  planId?: string
  address?: TaxPreviewAddress | null
  addressComplete?: boolean
  requiresAddress?: boolean
  refreshKey?: string
}

interface TaxPreviewResult {
  key: string
  preview: TaxPreview | null
}

function hasLocation(address?: TaxPreviewAddress | null, addressComplete = false) {
  return Boolean(address?.country && (addressComplete || address?.postal_code))
}

function buildBody(planId?: string, address?: TaxPreviewAddress | null, addressComplete = false) {
  return {
    ...(planId && { plan_id: planId }),
    ...(address &&
      hasLocation(address, addressComplete) && { address: R.pickBy(Boolean, address) }),
  }
}

export function useTaxPreview({
  enabled,
  planId,
  address,
  addressComplete = false,
  requiresAddress = false,
  refreshKey,
}: UseTaxPreviewParams) {
  const isAddressIncomplete = requiresAddress && !hasLocation(address, addressComplete)
  const isActive = enabled && !isAddressIncomplete
  const requestKey = useMemo(
    () => JSON.stringify([refreshKey ?? null, buildBody(planId, address, addressComplete)]),
    [refreshKey, planId, address, addressComplete]
  )
  const debouncedKey = useDebounce(requestKey, DEBOUNCE_MS)
  const [result, setResult] = useState<TaxPreviewResult | null>(null)
  const { fetch } = useAxios({
    url: 'billing/subscription/preview/',
    method: 'post',
    skip: true,
    timeout: REQUEST_TIMEOUT_MS,
  })

  useEffect(() => {
    if (!isActive) {
      setResult(null)
    }
  }, [isActive])

  useEffect(() => {
    const [, body] = JSON.parse(debouncedKey)
    if (!isActive || (requiresAddress && !body.address)) {
      return undefined
    }
    let isStale = false
    fetch({ data: body })
      .then((response: { data: TaxPreview }) => {
        if (!isStale) {
          setResult({ key: debouncedKey, preview: response.data })
        }
      })
      .catch(() => {
        if (!isStale) {
          setResult({ key: debouncedKey, preview: null })
        }
      })
    return () => {
      isStale = true
    }
  }, [isActive, requiresAddress, debouncedKey, fetch])

  const isLoading = isActive && result?.key !== requestKey
  if (enabled && isAddressIncomplete) {
    return { isLoading: false, preview: ADDRESS_INCOMPLETE_PREVIEW }
  }
  return { isLoading, preview: isLoading || !result ? null : result.preview }
}



// WEBPACK FOOTER //
// ./src/hooks/useTaxPreview/index.ts